import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone,
  faCode,
  faPlus,
  faRotateLeft,
  faTrashCan,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import {
  comparePlaceholders,
  getKeyStatus,
  KeyStatus,
  STATUS_LABEL,
} from './translations';

function warningsFor(state, status) {
  const warnings = [];
  if (status === KeyStatus.EMPTY) {
    warnings.push(
      'Leerer Wert – der Haupttext wird im Portal durch einen Leerstring ersetzt, nicht geerbt.',
    );
  }
  if (status === KeyStatus.IDENTICAL && !state.isNew) {
    warnings.push(
      'Gleicher Text wie in der Haupt-Übersetzung – der Override kann entfernt werden.',
    );
  }
  if (status === KeyStatus.UNKNOWN) {
    warnings.push(
      `Diesen Key gibt es in der Haupt-Übersetzung (${state.code}) nicht – wahrscheinlich ein Tippfehler oder ein entfernter Text.`,
    );
  }
  const placeholders = state.hasOverride
    ? comparePlaceholders(state.mainValue, state.value)
    : { missing: [], added: [] };
  if (placeholders.missing.length) {
    warnings.push(
      `Platzhalter fehlt: ${placeholders.missing.join(', ')} – der Wert wird im Portal unvollständig angezeigt.`,
    );
  }
  if (placeholders.added.length) {
    warnings.push(
      `Unbekannter Platzhalter: ${placeholders.added.join(', ')} – wird nicht ersetzt.`,
    );
  }
  return warnings;
}

/**
 * @name TranslationRowEditor
 * @description Panel below a key row: one block per language of the portal, so
 * a key can be kept in step across de/en/nl in one place.
 */
const TranslationRowEditor = ({
  path,
  languageStates,
  language,
  className,
  focusOnOpen = false,
  onAdopt,
  onCreateEmpty,
  onChange,
  onReset,
}) => {
  const currentRef = useRef(null);
  const [previewFor, setPreviewFor] = useState({});

  useEffect(() => {
    const input = currentRef.current;
    if (focusOnOpen && input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [focusOnOpen]);

  return (
    <div className="TranslationEditor-editor">
      {languageStates.map((state) => {
        const status = getKeyStatus({
          hasMain: state.hasMain,
          hasOverride: state.hasOverride,
          mainValue: state.mainValue,
          overrideValue: state.value,
        });
        const warnings = warningsFor(state, status);
        const isCurrent = state.code === language;
        const looksLikeHtml = /<[a-z][\s\S]*>/i.test(state.value || '');
        const rows = Math.min(
          14,
          Math.max(2, String(state.value || '').split('\n').length + 1),
        );

        return (
          <div
            key={state.code}
            className={`TranslationEditor-langBlock ${
              isCurrent ? 'current' : ''
            } ${state.isDirty ? 'dirty' : ''}`}
          >
            <div className="TranslationEditor-langHead">
              <span className="TranslationEditor-langCode">{state.code}</span>
              <span className={`TranslationEditor-badge status-${status}`}>
                {STATUS_LABEL[status]}
              </span>
              {state.isDirty && (
                <span className="TranslationEditor-badge dirty">
                  {state.isNew ? 'neu' : 'geändert'}
                </span>
              )}
              {state.error && (
                <span className="TranslationEditor-langError">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> Live-Datei
                  defekt
                </span>
              )}

              <div className="TranslationEditor-langActions">
                {state.hasMain && state.hasOverride && (
                  <button
                    type="button"
                    className="TranslationEditor-ghostButton"
                    onClick={() => onAdopt(path, false, state.code)}
                    title={`Haupttext von ${state.mainLanguage} einfügen`}
                  >
                    <FontAwesomeIcon icon={faClone} /> Haupttext
                  </button>
                )}
                {looksLikeHtml && (
                  <button
                    type="button"
                    className="TranslationEditor-ghostButton"
                    onClick={() =>
                      setPreviewFor((current) => ({
                        ...current,
                        [state.code]: !current[state.code],
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faCode} />
                    {previewFor[state.code] ? 'Vorschau aus' : 'Vorschau'}
                  </button>
                )}
                {state.hasOverride && (
                  <button
                    type="button"
                    className="TranslationEditor-ghostButton danger"
                    onClick={() => onReset(path, state.code)}
                    title={
                      state.isNew
                        ? 'Diesen neuen Override wieder verwerfen'
                        : `Override in ${state.code} entfernen – der Text kommt dann aus der Haupt-Übersetzung`
                    }
                  >
                    <FontAwesomeIcon
                      icon={state.isNew ? faRotateLeft : faTrashCan}
                    />
                    {state.isNew ? 'verwerfen' : 'entfernen'}
                  </button>
                )}
              </div>
            </div>

            <p className="TranslationEditor-standard">
              <span>
                Haupt
                {state.mainLanguage !== state.code
                  ? ` · ${state.mainLanguage}`
                  : ''}
              </span>
              {state.hasMain && state.mainValue !== ''
                ? state.mainValue
                : state.hasMain
                  ? 'im Hauptfile leer'
                  : 'Key gibt es im Hauptfile nicht'}
            </p>

            {state.hasOverride ? (
              <textarea
                ref={isCurrent ? currentRef : null}
                className="TranslationEditor-input"
                id={`override-${state.code}-${path}`}
                aria-label={`${className} · ${state.code}: ${path}`}
                value={state.value}
                rows={rows}
                spellCheck
                onChange={(event) =>
                  onChange(path, event.target.value, state.code)
                }
              />
            ) : (
              <div className="TranslationEditor-langEmpty">
                <span>
                  Kein eigener Text – {state.code} erbt den Haupttext.
                </span>
                <button
                  type="button"
                  className="TranslationEditor-ghostButton"
                  onClick={() => onAdopt(path, false, state.code)}
                  disabled={!state.hasMain}
                >
                  <FontAwesomeIcon icon={faPlus} /> mit Haupttext anlegen
                </button>
                <button
                  type="button"
                  className="TranslationEditor-ghostButton"
                  onClick={() => onCreateEmpty(path, state.code)}
                >
                  leer anlegen
                </button>
              </div>
            )}

            {previewFor[state.code] && (
              <div
                className="TranslationEditor-preview"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: state.value }}
              />
            )}

            {state.isDirty && !state.isNew && (
              <p className="TranslationEditor-was">
                bisher live: {state.baseValue || '(leer)'}
              </p>
            )}

            {warnings.map((warning) => (
              <p className="TranslationEditor-warning" key={warning}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {warning}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

TranslationRowEditor.propTypes = {
  path: PropTypes.string.isRequired,
  languageStates: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      mainValue: PropTypes.string.isRequired,
      mainLanguage: PropTypes.string.isRequired,
      hasMain: PropTypes.bool.isRequired,
      hasOverride: PropTypes.bool.isRequired,
      value: PropTypes.string.isRequired,
      baseValue: PropTypes.string,
      isNew: PropTypes.bool,
      isDirty: PropTypes.bool,
      error: PropTypes.string,
    }),
  ).isRequired,
  language: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  focusOnOpen: PropTypes.bool,
  onAdopt: PropTypes.func.isRequired,
  onCreateEmpty: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default TranslationRowEditor;
